import { Text, Divider, useToast, useModal } from '@afterpaytouch/core'
import { unwrapResult } from '@reduxjs/toolkit'
import { useTranslation } from 'next-i18next'
import React, { ReactNode, useContext, useEffect } from 'react'
import {
  DisabledModal,
  Head,
  BillingAgreementMessage,
  Donation,
  ModuleContainer,
  ProfileCard,
  ShippingMessage,
  ShippingSummary,
  OrderSummary,
  OAuthLoginConsentModal,
  PaymentScheduleContainer,
  PaymentOptionsErrorModal,
  SummaryNotification,
  ConsumerLendingErrorModal,
  BiometricRegistrationModal,
  SummaryCtaFooter,
  PPAModal,
  SummaryNotificationContext,
  SummaryNotificationProvider,
} from '../../component'
import { Route } from '../../config/router'
import { useFlag } from '@afterpaytouch/feature'
import {
  ApplePayStatus,
  CheckoutStatus,
  PaymentMethodType,
  getCheckpoint,
  Features,
  nonShippingInfoAddressModes,
  ExperimentValue,
  getIdentityVerificationRoute,
  getPayIn2Experiment,
  CheckoutPage,
  BiometricRegistrationModalKey,
} from '../../model'
import { PaymentMethodTypes, TrackingEvent } from '../../model/monitoring'
import {
  confirmCheckout,
  selectPurchaseMethod,
  setCardScanCheckpoint,
  useAppDispatch,
  useAppRouter,
  useApplePay,
  useApplePayState,
  useCheckoutError,
  useCheckoutState,
  useIsDeferredShipping,
  useIsExpress,
  useIsReadyToConfirm,
  useIsShippingOptionValid,
  useSelectedPaymentMethod,
  useIsBillingAgreementApprovalProcess,
  useIsTrustedCheckout,
  useAuthentication,
  useIsPickup,
  useCountry,
  useShouldShowCashAppTooltip,
  useInitialData,
  useIsBiometricConflictingModal,
  useIsEligibleBiometricRegistration,
  setDisplayProcessingModal,
  useOAuthAuthorizationFlow,
  useExperiment,
  shouldShowOAuthConsentForm,
  acceptOAuthConsent,
  declineOAuthConsent,
  useCLRiskDeclineError,
  setStartConfirmTime,
  setEndConfirmTime,
  useIsAfterpayAppGiftCardFlow,
  useHasSavedPaymentMethods,
  resetCheckoutError,
  useSummaryImprovementsExperiment,
  useCheckoutToken,
  useIsUpdatePreferredPaymentDay,
} from '../../state'
import { isPortalApiError } from '@afterpaytouch/portal-api/utils'
import { useCheckoutSizes } from '../../utils/responsive'
import { create } from 'react-modal-promise'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { TrackPageViewed, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { usePostCheckoutTriggerExperiment } from '../../utils/post-checkout'
import { getConfirmCheckoutErrorHandling } from '../../utils/redirects'
import { ErrorCode, FirstPaymentType } from '@afterpaytouch/portal-api'
import { sendPurchaseEvent } from '../../integrations/googletagmanager/utils'
import { RedesignedSummaryPage } from '../redesigned-summary'
import { useIsRedesignedSummaryPage } from '../../utils/convergence'
import { SIZES } from '../../component/ModuleContainer/types'

export const BaseSummaryPage: CheckoutPage = () => {
  const { notification, setClicked, clicked } = useContext(SummaryNotificationContext)
  const { t } = useTranslation()
  const router = useAppRouter()
  const dispatch = useAppDispatch()
  const country = useCountry()
  const { status, firstPaymentType, started, checkout, error } = useCheckoutState()
  const isExpress = useIsExpress()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const applepay = useApplePayState()
  const ready = useIsReadyToConfirm()
  const applePayHelper = useApplePay()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const checkoutError = useCheckoutError()
  const isDeferredShipping = useIsDeferredShipping()

  const shouldUpdatePreferredPaymentDay = useIsUpdatePreferredPaymentDay()
  const { isError: isPclError } = useCLRiskDeclineError()
  const isShippingOptionValid = useIsShippingOptionValid()

  const { shouldShowCashAppAccountLinkingToast, saveToTags } = useShouldShowCashAppTooltip()
  const { addressMode } = useInitialData()
  const cashTooltipExperiment = useExperiment('CheckoutSummaryCashTooltip')
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()

  // biometrics
  const isConflictingModalsWithBiometric = useIsBiometricConflictingModal()
  const isEligibleBiometricRegistration = useIsEligibleBiometricRegistration()
  const { showModal: showBiometricModal, closeModal: closeBiometricModal } = useModal(BiometricRegistrationModalKey)

  // post checkout trigger experiment
  usePostCheckoutTriggerExperiment()

  const isAfterpayAppGiftCardFlow = useIsAfterpayAppGiftCardFlow(true)

  const isSummaryButtonDisabled = (!ready || clicked || typeof selectedPaymentMethod === 'undefined' || notification?.severe) ?? false

  // add first payment experiment
  const addFirstPaymentMethodExperimentVariant = useFlag<ExperimentValue>(Features.CHECKOUT_ADD_FIRST_PAYMENT_METHOD, 'E')
  const isAddFirstPaymentMethodExperimentBEligible = addFirstPaymentMethodExperimentVariant === 'B' && status === CheckoutStatus.FirstPaymentMethodRequired

  // PayIn2 experiment
  const payIn2ExperimentValue = useFlag(Features.CHECKOUT_PAY_IN_2_AMOUNT, -1)
  useExperiment('PayIn2Experiment', !isExpress, getPayIn2Experiment(payIn2ExperimentValue))

  // Summary Page Improvements experiment
  const { enabled: summaryImprovementsEnabled, value: summaryImprovementsVariant } = useSummaryImprovementsExperiment()
  const hideProfileCardExperiment = summaryImprovementsEnabled && (summaryImprovementsVariant === 'B' || summaryImprovementsVariant === 'E')
  const showProfileCard = status >= CheckoutStatus.Starting && !hideProfileCardExperiment

  const toast = useToast()

  useEffect(() => {
    if (typeof error !== 'undefined' && typeof error.extra !== 'undefined') {
      const { extra } = error
      if (extra.isEFAFinished) {
        dispatch(resetCheckoutError())
      }
    }
  }, [error])

  useEffect(() => {
    if (shouldShowCashAppAccountLinkingToast) {
      toast({
        message: () => (
          <>
            <Text size={textSize} bold>
              {t('common:firstPaymentWithCash:heading')}
            </Text>
            <Text size={textSize}>
              {t(cashTooltipExperiment.control ? 'common:firstPaymentWithCash:content' : 'common:firstPaymentWithCash:contentExperiment')}
            </Text>
          </>
        ),
        kind: 'Success',
        toastTime: 5000,
        swipeToDismiss: true,
      })
      saveToTags()
    }
  }, [shouldShowCashAppAccountLinkingToast, saveToTags])

  const isBillingAgreementApprovalProcess = useIsBillingAgreementApprovalProcess()
  const { sectionHeadingSize, textSize } = useCheckoutSizes()
  const isPickup = useIsPickup()

  const { authenticated } = useAuthentication()
  const isTrusted = useIsTrustedCheckout()
  // Logout experiment removed. More info: https://square.slack.com/archives/C03282XR4D7/p1673242190900099?thread_ts=1673230597.741079&cid=C03282XR4D7
  const displayLogout = false

  // OAuth
  const { oAuthAuthorization } = useOAuthAuthorizationFlow()
  const showPersistentLoginConsentForm = create(({ isOpen, onResolve, onReject }) => (
    <OAuthLoginConsentModal show={isOpen} onDecline={onReject} onAccept={onResolve} />
  ))

  // This is purely for analytics and should not be used elsewhere. The FE needs to differentiate between no downpayment and some downpayment but amplitude does not care.
  const analyticsFirstPaymentType = typeof firstPaymentType !== 'undefined' && firstPaymentType !== '' ? firstPaymentType : FirstPaymentType.PaymentUpfront
  const token = useCheckoutToken()

  const moduleContainerStyle = {
    ...{ padding: { vertical: SIZES.LARGE, horizontal: SIZES.XLARGE } },
    reducedHeight: isAfterpayAppGiftCardFlow,
  }

  const finaliseCheckout = async (): Promise<void> => {
    try {
      const confirmResponse = unwrapResult(await dispatch(confirmCheckout({ shouldUpdatePreferredPaymentDay })))

      await oAuthAuthorization(isTrusted)
      dispatch(setEndConfirmTime(performance.now()))

      if (confirmResponse.status !== 'FAILURE') {
        const orderAmount = started?.consumerAmount ?? checkout?.amount
        sendPurchaseEvent(token, !hasSavedPaymentMethods, orderAmount)

        dispatch(setDisplayProcessingModal(true))
      } else {
        router.push(Route.TERMINAL_ERROR)
      }
    } catch (e) {
      if (isPortalApiError(e)) {
        const { route, messageKey } = getConfirmCheckoutErrorHandling({
          errorCode: e.errorCode,
          returnRoute: Route.ENTER_PASSWORD,
        })
        if (Route.CARD_SCAN === route) {
          dispatch(setCardScanCheckpoint(getCheckpoint(e.message)))
        }
        if (typeof messageKey === 'string') {
          toast({
            message: t(messageKey),
            kind: 'Failure',
          })
          dispatch(resetCheckoutError())
        }

        if (route) {
          setClicked(false)
          router.push(route ?? Route.TERMINAL_ERROR)
        }
      } else {
        capture.warning('Unexpected error during payment confirmation', { ...e })
      }
    }
  }

  const handleConfirmClick = async (): Promise<void> => {
    if (isSummaryButtonDisabled) {
      logEvent(TrackingEvent.PRESSED_SUMMARY_PAGE_CONFIRM_BUTTON_DISABLED)
      return
    }

    setClicked(true)
    logEvent(TrackingEvent.PRESSED_SUMMARY_CONFIRM_BUTTON, {
      paymentMethodType: PaymentMethodTypes[selectedPaymentMethod?.type as PaymentMethodType],
    })

    dispatch(setStartConfirmTime(performance.now()))

    await dispatch(selectPurchaseMethod(selectedPaymentMethod))

    if (selectedPaymentMethod?.type === PaymentMethodType.APPLE_PAY) {
      applePayHelper.initialise(t, logEvent)
      try {
        await applePayHelper.startSession()
      } catch (e) {
        return
      }
    }

    await finaliseCheckout()
  }

  const pageTitle = `${t('summary:reviewOrder')} | ${t('common:brandName')}`

  useEffect(() => {
    if (applepay.status === ApplePayStatus.Cancelled) {
      setClicked(false)
    }
  }, [applepay.status])

  useEffect(() => {
    if (status >= CheckoutStatus.Started && !ready) {
      logEvent(TrackingEvent.CHECKOUT_CONFIRM_DISABLED, {
        checkoutStatus: status,
        checkoutError: checkoutError?.error,
        isShippingOptionValid,
      })
    }
  }, [ready])

  useEffect(() => {
    if (isConflictingModalsWithBiometric) {
      closeBiometricModal()
    }
  }, [isConflictingModalsWithBiometric])

  useEffect(() => {
    if (status <= CheckoutStatus.Loaded) {
      router.push(Route.LOGGED_IN)
    }

    // TODO: do we need to handle CheckoutStatus.Starting
    // Route to verify-identity if back button is clicked on [document]
    if (status === CheckoutStatus.IdentityIneligible) {
      logEvent(TrackingEvent.TRIGGER_IDENTITY_REDIRECT_SUMMARY_PAGE)
      const url = getIdentityVerificationRoute(country, {}, error?.errorCode as ErrorCode) as Route
      router.push(url)
    }

    if (status === CheckoutStatus.LackOfEligiblePaymentMethod) {
      router.push(Route.ADD_PAYMENT_METHOD)
    }

    router.prefetch(Route.PAYMENT_METHOD)
  }, [])

  useEffect(() => {
    const showOauthConsent = async (): Promise<void> => {
      const shouldShowOAuthConsent = await dispatch(shouldShowOAuthConsentForm)
      await (shouldShowOAuthConsent
        ? showPersistentLoginConsentForm()
            .then(() => {
              dispatch(acceptOAuthConsent)
            })
            .catch(() => {
              dispatch(declineOAuthConsent)
            })
        : Promise.resolve())
    }
    showOauthConsent()
  }, [])

  useEffect(() => {
    if (isEligibleBiometricRegistration) {
      showBiometricModal()
    }
  }, [isEligibleBiometricRegistration])

  return (
    <>
      <BiometricRegistrationModal />
      {isPclError && <ConsumerLendingErrorModal isError={isPclError} />}
      {status !== CheckoutStatus.Error && (
        <>
          <Head title={pageTitle} />
          <PaymentOptionsErrorModal />
          {showProfileCard && (
            <ModuleContainer {...moduleContainerStyle}>
              <ProfileCard allowLogout={displayLogout} authLogout={authenticated} />
            </ModuleContainer>
          )}
          {status >= CheckoutStatus.Started && (
            <>
              {isExpress && (
                <>
                  {!isDeferredShipping && (
                    <Text size={sectionHeadingSize} renderAs='div' bold>
                      {isPickup ? t('common:orderCollectionDetails') : t('common:chooseShippingOptions')}
                    </Text>
                  )}
                  <ShippingSummary />
                </>
              )}
              {!isBillingAgreementApprovalProcess && (
                <>
                  <OrderSummary />
                  {!isAfterpayAppGiftCardFlow && <Divider kind='Hairline' />}
                  {!nonShippingInfoAddressModes.includes(addressMode) && <ShippingMessage />}
                </>
              )}
              <PaymentScheduleContainer isSummary testNameSpace={'summary-page'} />
              <SummaryNotification />
              <BillingAgreementMessage />
              <PPAModal />
              <Donation />
              {!isAddFirstPaymentMethodExperimentBEligible && (
                <SummaryCtaFooter
                  clicked={clicked}
                  isSummaryButtonDisabled={isSummaryButtonDisabled}
                  notification={notification}
                  onConfirm={handleConfirmClick}
                />
              )}
              <TrackPageViewed
                eventProperties={{
                  firstPaymentType: analyticsFirstPaymentType,
                  summaryPageVersion: summaryImprovementsEnabled && summaryImprovementsVariant === 'E' ? 'current ap v0' : 'legacy',
                }}
              />
              {clicked && <DisabledModal />}
            </>
          )}
        </>
      )}
    </>
  )
}

export const SummaryPage: CheckoutPage = () => {
  const isRedesignedSummaryPage = useIsRedesignedSummaryPage()

  const renderSummaryPage = (): ReactNode => {
    if (isRedesignedSummaryPage) {
      return <RedesignedSummaryPage />
    }

    return <BaseSummaryPage />
  }

  return <SummaryNotificationProvider>{renderSummaryPage()}</SummaryNotificationProvider>
}
